import { useState } from 'react';
import { useProvince } from '../contexts/ProvinceContext';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useRankings } from '../hooks/useRankings';
import { DesktopRankingsShell } from '../components/rankings/DesktopRankingsShell';
import { MobileRankingsShell } from '../components/rankings/MobileRankingsShell';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { COLORS } from '../constants/theme';
import type { RankingEntityType, RankingScope, RankingStatKey } from '../types/rankings';

const ENTITY_TABS: readonly { key: RankingEntityType; label: string }[] = [
  { key: 'province', label: 'Provinces' },
  { key: 'kingdom', label: 'Kingdoms' },
];

/**
 * SF-159 Component 1: Rankings — new page built for both platforms at once, not a mobile-content
 * migration of an existing desktop page like the other four World-section siblings (SF-156/157/
 * 158). Registered under the legacy `<Layout />` route group.
 *
 * Sole owner of all filter state (Phase 3 Critical Issue 4): `entityType`/`scope`/`sortBy`.
 * `islandNumber` is derived (the viewer's own island), not independent state. Switching the entity
 * tab preserves `scope`/`sortBy` unchanged — both axes are meaningful for either entity type, so
 * there is no reset-on-switch effect.
 *
 * Structure matches the approved mockup: entity tablist above one `role="tabpanel"` wrapper that
 * contains the scope radiogroup *and* the active shell together — the radiogroup is rendered once
 * here (not per-shell) since its markup/behavior is identical on both platforms.
 */
export function Rankings() {
  const { province } = useProvince();
  const platform = useAppShellPlatform();

  const [entityType, setEntityType] = useState<RankingEntityType>('province');
  const [scope, setScope] = useState<RankingScope>('world');
  const [sortBy, setSortBy] = useState<RankingStatKey>('networth');

  const islandNumber = province?.metadata?.location?.island ?? null;

  const { data, loading, error } = useRankings(entityType, scope, sortBy, scope === 'island' ? islandNumber : null);

  const activeTabId = `rankings-tab-${entityType}`;
  const panelId = `rankings-panel-${entityType}`;

  const shellProps = data && {
    entries: data.entries,
    viewerEntry: data.viewerEntry,
    totalCount: data.totalCount,
    currentTick: data.currentTick,
    sortBy,
    onSortChange: setSortBy,
    loading,
    error,
  };

  return (
    <div>
      <h1 className="text-lg font-bold text-game-50 mb-1">
        Rankings{' '}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: COLORS.text.tertiary,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            padding: '2px 7px',
            verticalAlign: 'middle',
          }}
        >
          <span aria-hidden="true" style={{ color: COLORS.text.muted, fontSize: 8 }}>●</span>
          Stub data
        </span>
      </h1>
      <p className="mb-4" style={{ fontSize: 13, color: COLORS.text.tertiary }}>
        World and island standings — Land, Net Worth, Honour, and Wars Won
      </p>

      <div role="tablist" aria-label="Ranking entity" className="flex gap-1">
        {ENTITY_TABS.map((tab) => {
          const isActive = tab.key === entityType;
          return (
            <button
              key={tab.key}
              id={`rankings-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`rankings-panel-${tab.key}`}
              onClick={() => setEntityType(tab.key)}
              style={{
                minHeight: 44,
                fontSize: 13.5,
                fontWeight: 600,
                padding: '9px 18px',
                borderRadius: '7px 7px 0 0',
                border: `1px solid ${COLORS.border}`,
                borderBottom: isActive ? 'none' : `1px solid ${COLORS.border}`,
                backgroundColor: isActive ? COLORS.card : COLORS.cardAlt,
                color: isActive ? COLORS.text.primary : COLORS.text.tertiary,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={activeTabId}
        style={{
          backgroundColor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '0 8px 8px 8px',
          padding: 18,
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div
            role="radiogroup"
            aria-label="Scope"
            style={{
              display: 'inline-flex',
              backgroundColor: COLORS.cardAlt,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              padding: 2,
              gap: 6,
            }}
          >
            <button
              type="button"
              role="radio"
              aria-checked={scope === 'world'}
              onClick={() => setScope('world')}
              style={{
                minHeight: 44,
                fontSize: 12.5,
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: 4,
                border: 'none',
                backgroundColor: scope === 'world' ? COLORS.sectionHeader : 'transparent',
                color: scope === 'world' ? COLORS.text.primary : COLORS.text.tertiary,
                cursor: 'pointer',
              }}
            >
              World
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={scope === 'island'}
              onClick={() => setScope('island')}
              style={{
                minHeight: 44,
                fontSize: 12.5,
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: 4,
                border: 'none',
                backgroundColor: scope === 'island' ? COLORS.sectionHeader : 'transparent',
                color: scope === 'island' ? COLORS.text.primary : COLORS.text.tertiary,
                cursor: 'pointer',
              }}
            >
              My Island{islandNumber != null ? ` (Island ${islandNumber})` : ''}
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        )}

        {!loading && error && (
          <div>
            <ErrorMessage message={error} />
            <div className="mt-4 text-center">
              {/* No real refresh() exists on this synchronous mock hook — this branch is
                  forward-compatible dead code today (see spec's State Handling section); a future
                  async useRankings would wire this to its own refresh(). */}
              <button
                type="button"
                onClick={() => {}}
                className="px-4 py-2 rounded"
                style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary, minHeight: 44 }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!loading && !error && shellProps && (
          platform === 'mobile' ? (
            <MobileRankingsShell {...shellProps} />
          ) : (
            <DesktopRankingsShell {...shellProps} />
          )
        )}
      </div>
    </div>
  );
}
